// Counterpart of webapp/src/store/boards.ts (redux slice -> zustand store).

import {create} from 'zustand'
import {Board} from '../types/board'
import client from '../api/client'

type BoardsState = {
    boards: Record<string, Board>
    recentBoardIds: string[]
    loading: boolean
    error: string | null

    loadBoards: (teamId: string) => Promise<Board[]>
    loadBoard: (boardId: string) => Promise<Board | undefined>
    upsertBoards: (boards: Board[]) => void
    upsertBoard: (board: Board) => void
    removeBoard: (boardId: string) => void
    touchRecentBoard: (boardId: string) => void
    createBoard: (board: Board) => Promise<Board>
}

export const useBoards = create<BoardsState>((set, get) => ({
    boards: {},
    recentBoardIds: [],
    loading: false,
    error: null,

    async loadBoards(teamId) {
        set({loading: true, error: null})
        const boards = await client.getBoards(teamId)
        set((state) => {
            const next = {...state.boards}
            boards.forEach((b) => {
                next[b.id] = b
            })
            return {boards: next, loading: false}
        })
        return boards
    },

    async loadBoard(boardId) {
        const existing = get().boards[boardId]
        if (existing) {
            return existing
        }
        const board = await client.getBoard(boardId)
        if (board) {
            get().upsertBoard(board)
        }
        return board
    },

    upsertBoards(boards) {
        set((state) => {
            const next = {...state.boards}
            boards.forEach((b) => {
                next[b.id] = b
            })
            return {boards: next}
        })
    },

    upsertBoard(board) {
        get().upsertBoards([board])
    },

    removeBoard(boardId) {
        set((state) => {
            const boards = {...state.boards}
            delete boards[boardId]
            return {boards, recentBoardIds: state.recentBoardIds.filter((id) => id !== boardId)}
        })
    },

    touchRecentBoard(boardId) {
        set((state) => ({
            recentBoardIds: [boardId, ...state.recentBoardIds.filter((id) => id !== boardId)].slice(0, 8),
        }))
    },

    async createBoard(board) {
        const created = await client.createBoard(board)
        get().upsertBoard(created)
        return created
    },
}))

export const getBoard = (state: BoardsState, boardId: string | undefined): Board | undefined =>
    (boardId ? state.boards[boardId] : undefined)
